import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { studentPersonaQueryKey, useStudentPersona, useStudentPersonaSuspense } from "../queries";
import { Avatar, AvatarProps } from "@mantine/core";
import { getPortalsApiResourceUrl } from "@services/api/portalApi";

type StudentPhotoProps = { id: string; size?: number; avatarProps?: AvatarProps };
type StudentPersonaData = ReturnType<typeof useStudentPersona>["data"];

type ImageLoadStatus = "pending" | "loaded" | "failed";
type ImageLoadEntry = { status: ImageLoadStatus; promise?: Promise<void> };

const imageLoadCache = new Map<string, ImageLoadEntry>();

function suspendUntilImageReady(src: string) {
    const cached = imageLoadCache.get(src);
    if (!cached) {
        const entry: ImageLoadEntry = { status: "pending" };
        entry.promise = new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => {
                entry.status = "loaded";
                resolve();
            };
            img.onerror = () => {
                entry.status = "failed";
                resolve();
            };
            img.src = src;
        });
        imageLoadCache.set(src, entry);
        throw entry.promise;
    }
    if (cached.status === "pending" && cached.promise) {
        throw cached.promise;
    }
}

const StudentPhotoInner: React.FC<StudentPhotoProps & { suspense: boolean; data: StudentPersonaData; isLoading: boolean }> = ({ id, size = 32, avatarProps, suspense, data, isLoading }) => {
    const qc = useQueryClient();

    // Track failed URLs so we don’t keep trying the same broken URL.
    const failedUrlsRef = React.useRef<Set<string>>(new Set());

    // Track whether we've already tried refreshing metadata for the current URL.
    const refreshedForUrlRef = React.useRef<string | null>(null);

    const photoUrl = data?.photo?.url ?? null;
    // Initialize from the current photo URL to avoid a post-mount src flip
    // that can trigger a visible Suspense fallback flicker.
    const [imgSrc, setImgSrc] = React.useState<string | null>(() => photoUrl);

    // When we get a NEW photoUrl, try it (unless we've already marked it failed).
    React.useEffect(() => {
        if (!photoUrl) {
            setImgSrc(null);
            return;
        }

        if (failedUrlsRef.current.has(photoUrl)) {
            setImgSrc(null);
            return;
        }

        setImgSrc(photoUrl);
        // allow one refresh attempt for this new url if it errors
        refreshedForUrlRef.current = null;
    }, [photoUrl]);

    if (isLoading || !data) {
        return (
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <div style={{ width: size, height: size, borderRadius: "50%", background: "rgba(0,0,0,0.08)" }} />
            <div style={{ width: 140, height: 12, borderRadius: 6, background: "rgba(0,0,0,0.08)" }} />
        </div>
        );
    }

    const fullImgSrc = imgSrc ? getPortalsApiResourceUrl(imgSrc) : undefined;

    if (suspense && fullImgSrc) {
        suspendUntilImageReady(fullImgSrc);
    }

    return (
        <Avatar 
            name={`${data.firstName} ${data.lastName}`} 
            color='initials'
            radius={0}
            styles={{
                root: {
                    width: "100%",
                    height: "100%",
                },
            }}
            src={fullImgSrc}
            onError={() => {
                // Mark current photoUrl as failed so we don't flip back to it.
                if (photoUrl) failedUrlsRef.current.add(photoUrl);

                // Always show fallback immediately.
                setImgSrc(null);

                // If this might be an expired sig, re-mint once (per URL).
                if (photoUrl && refreshedForUrlRef.current !== photoUrl) {
                    refreshedForUrlRef.current = photoUrl;
                    qc.invalidateQueries({ queryKey: studentPersonaQueryKey(id) });
                }
            }}
            {...avatarProps}
        />
    );
};

export const StudentPhoto: React.FC<StudentPhotoProps> = (props) => {
    const { data, isLoading } = useStudentPersona(props.id);
    return <StudentPhotoInner {...props} suspense={false} data={data} isLoading={isLoading} />;
};

export const StudentPhotoSuspense: React.FC<StudentPhotoProps> = (props) => {
    const { data } = useStudentPersonaSuspense(props.id);
    return <StudentPhotoInner {...props} suspense={true} data={data} isLoading={false} />;
};
